//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import HelpManager from '@/components/Help/HelpManager';
import ProtectedRoute from '@/components/ProtectedRoute';
import RootErrorPage from '@/components/Errors/RootErrorPage';
import { ERoutesRoot } from './RoutesEnum';

// Helper to handle lazy loading.
const lazyComp = (load: () => Promise<{ default: React.ComponentType }>) =>
  async () => ({ Component: (await load()).default });

export const router = createBrowserRouter([{
  path: '/',
  // HelpManager must render <Outlet />.
  element: <HelpManager />,
  // Shown during the initial load while the matched route's lazy chunk
  // resolves; without it React Router warns about a missing HydrateFallback.
  hydrateFallbackElement: (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh'
    }}>
      <CircularProgress aria-label="Loading" />
    </Box>
  ),
  errorElement: <RootErrorPage />,
  children: [
    {
      // === Public Routes ===
      // We use <Outlet /> as the element so we can attach the Error Page
      // to this specific group of routes.
      element: <Outlet />,
      errorElement: <RootErrorPage />,
      children: [
        { path: ERoutesRoot.TermsOfUse,
          lazy: lazyComp(() => import('@/pages/TermsOfUse')) },
        { path: ERoutesRoot.Login,
          lazy: lazyComp(() => import('@/pages/Login')) },
        { path: ERoutesRoot.LaunchNotes,
          lazy: lazyComp(() => import('@/pages/LaunchNotes')) },
      ],
    },
    {
      // === Protected Routes ===
      // If a crash happens inside ProtectedRoute or any child,
      // RootErrorPage will catch it.
      element: <ProtectedRoute />,
      errorElement: <RootErrorPage />,
      children: [
        { path: ERoutesRoot.Dashboard,
          lazy: lazyComp(() => import('@/pages/Dashboard')) },
        { path: ERoutesRoot.Datasets,
          lazy: lazyComp(() => import('@/pages/Datasets')) },
        { path: ERoutesRoot.Graphs,
          lazy: lazyComp(() => import('@/pages/Graphs')) },
        // Legacy bookmarks for the two pages Graphs combines.
        { path: ERoutesRoot.Schemas,
          element: <Navigate to={ERoutesRoot.Graphs} replace /> },
        { path: ERoutesRoot.Upload,
          element: <Navigate to={ERoutesRoot.Graphs} replace /> },
        { path: ERoutesRoot.Jobs,
          lazy: lazyComp(() => import('@/pages/Jobs')) },
        { path: ERoutesRoot.Explorer,
          lazy: lazyComp(() => import('@/pages/Explorer')) },
        { path: `${ERoutesRoot.Explorer}/:jobId`,
          lazy: lazyComp(() => import('@/pages/Explorer')) },
      ],
    },
  ],
}]);
